// src/pages/NetworkAlerts/AlertsTab.js — aba "Alertas" (lista de alertas ativos/encerrados)
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";

import EmptyState from "../../components/EmptyState";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { StatusBadge } from "../../components/ui";
import { i18n } from "../../translate/i18n";
import { useDate } from "../../hooks/useDate";

import useStyles from "./styles";
import { ALERT_FILTERS, scopeLabel, sortAlerts } from "./constants";

const COLUMNS = 8;

// Ativo com a validade vencida: o bot já não mostra o aviso, mas o alerta segue aberto.
const isExpired = (alert) => {
  if (!alert.active || !alert.endsAt) return false;
  const ends = new Date(alert.endsAt).getTime();
  return !Number.isNaN(ends) && ends < Date.now();
};

const AlertsTab = ({
  alerts,
  loading,
  filter,
  onFilterChange,
  hasErp,
  onCreate,
  onEdit,
  onClose,
  onBroadcast,
  onDelete,
}) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();
  const list = sortAlerts(alerts);
  // Menu "⋮" da linha: { anchor, alert }
  const [rowMenu, setRowMenu] = useState(null);

  // Fecha o menu e dispara a mesma ação que o ícone solto disparava.
  const fromMenu = (action) => () => {
    const alert = rowMenu?.alert;
    setRowMenu(null);
    if (alert) action(alert);
  };

  const renderStatus = (alert) => {
    if (!alert.active) {
      return <StatusBadge color="neutral" label={i18n.t("networkAlerts.card.status.closed")} />;
    }
    if (isExpired(alert)) {
      return <StatusBadge color="neutral" label={i18n.t("networkAlerts.card.expired")} />;
    }
    return <StatusBadge color="success" label={i18n.t("networkAlerts.card.status.active")} />;
  };

  const renderEmpty = () => {
    if (!hasErp) {
      return (
        <EmptyState
          icon={<LinkOffIcon />}
          title={i18n.t("networkAlerts.empty.noErp.title")}
          message={i18n.t("networkAlerts.empty.noErp.message")}
          action={
            <Button component={RouterLink} to="/queue-integration" variant="outlined">
              {i18n.t("networkAlerts.buttons.goToIntegrations")}
            </Button>
          }
        />
      );
    }
    if (filter !== "active") {
      return (
        <EmptyState
          icon={<NotificationsNoneIcon />}
          title={i18n.t("networkAlerts.empty.noAlertsFiltered.title")}
          message={i18n.t("networkAlerts.empty.noAlertsFiltered.message")}
        />
      );
    }
    return (
      <EmptyState
        icon={<NotificationsNoneIcon />}
        title={i18n.t("networkAlerts.empty.noAlerts.title")}
        message={i18n.t("networkAlerts.empty.noAlerts.message")}
        action={
          <>
            <ol className={classes.steps}>
              {[1, 2, 3].map((n) => (
                <li key={n} className={classes.step}>
                  <span className={classes.stepNumber}>{n}</span>
                  <span>{i18n.t(`networkAlerts.empty.noAlerts.step${n}`)}</span>
                </li>
              ))}
            </ol>
            <Button variant="contained" color="primary" onClick={onCreate}>
              {i18n.t("networkAlerts.buttons.newAlert")}
            </Button>
          </>
        }
      />
    );
  };

  return (
    <div className={classes.tabPanel}>
      <div className={classes.filterBar} role="group" aria-label="Filtrar alertas">
        {ALERT_FILTERS.map((key) => (
          <Chip
            key={key}
            clickable
            label={i18n.t(`networkAlerts.filters.${key}`)}
            variant="outlined"
            className={filter === key ? classes.filterChipActive : undefined}
            aria-pressed={filter === key}
            onClick={() => onFilterChange(key)}
          />
        ))}
      </div>

      <Menu
        anchorEl={rowMenu?.anchor || null}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={fromMenu(onBroadcast)}>
          <ListItemIcon className={classes.menuIcon}>
            <SendOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("networkAlerts.buttons.broadcast")}
        </MenuItem>
        {rowMenu?.alert?.active && (
          <MenuItem onClick={fromMenu(onClose)}>
            <ListItemIcon className={classes.menuIcon}>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            {i18n.t("networkAlerts.buttons.close")}
          </MenuItem>
        )}
        <Divider component="li" />
        <MenuItem onClick={fromMenu(onDelete)} className={classes.menuDanger}>
          <ListItemIcon className={classes.menuIcon}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("networkAlerts.buttons.delete")}
        </MenuItem>
      </Menu>

      <Paper variant="outlined" className={classes.card}>
        {/* vazio fora da tabela: dentro dela o aviso ficaria centrado na largura mínima, fora da tela no celular */}
        {!loading && list.length === 0 ? (
          renderEmpty()
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small" stickyHeader className={classes.alertsTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Alerta</TableCell>
                  <TableCell>{i18n.t("networkAlerts.form.scopeType")}</TableCell>
                  <TableCell>{i18n.t("networkAlerts.card.startsAt")}</TableCell>
                  <TableCell>{i18n.t("networkAlerts.card.endsAt")}</TableCell>
                  <TableCell align="right">{i18n.t("networkAlerts.card.affected")}</TableCell>
                  <TableCell>{i18n.t("networkAlerts.card.createdBy")}</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell className={classes.alertCell}>
                      <div className={classes.alertTitle}>
                        <span className={classes.alertTitleText} title={alert.title}>
                          {alert.title}
                        </span>
                        {alert.mediaUrl && (
                          <StatusBadge
                            color="neutral"
                            size="sm"
                            icon={<ImageOutlinedIcon style={{ fontSize: 13 }} />}
                            label={i18n.t("networkAlerts.card.hasPhoto")}
                          />
                        )}
                      </div>
                      <div className={classes.alertMessage} title={alert.message}>
                        {alert.message}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        color="neutral"
                        dot={false}
                        label={scopeLabel(alert.scopeType, alert.scopeValue)}
                      />
                      {alert.erpIntegration?.name && (
                        <span className={classes.secondaryLine} title={i18n.t("networkAlerts.card.erp")}>
                          {alert.erpIntegration.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.cellSecondary} ${classes.nowrap}`}>
                      {alert.startsAt ? datetimeToClient(alert.startsAt) : "—"}
                    </TableCell>
                    <TableCell className={`${classes.cellSecondary} ${classes.nowrap}`}>
                      {alert.endsAt ? datetimeToClient(alert.endsAt) : i18n.t("networkAlerts.card.noEnds")}
                    </TableCell>
                    <TableCell align="right">
                      {alert.affectedCount != null ? alert.affectedCount : "—"}
                    </TableCell>
                    <TableCell className={classes.cellSecondary}>
                      {alert.createdByUser?.name || "—"}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {renderStatus(alert)}
                      {alert.broadcastCampaignId && (
                        <RouterLink to="/invoice-campaigns" className={classes.campaignLink}>
                          {i18n.t("networkAlerts.card.campaign", { id: alert.broadcastCampaignId })}
                        </RouterLink>
                      )}
                    </TableCell>
                    <TableCell align="right" className={classes.nowrap}>
                      <Tooltip title={i18n.t("networkAlerts.buttons.edit")}>
                        <IconButton
                          size="small"
                          aria-label={i18n.t("networkAlerts.buttons.edit")}
                          onClick={() => onEdit(alert)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mais ações">
                        <IconButton
                          size="small"
                          aria-label="Mais ações"
                          aria-haspopup="true"
                          onClick={(e) => setRowMenu({ anchor: e.currentTarget, alert })}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && list.length === 0 && <TableRowSkeleton columns={COLUMNS} />}
              </TableBody>
            </Table>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default AlertsTab;
